/**
 * Carousels API Utilities
 * Handles CRUD operations for carousel persistence.
 * 
 * URL CONTRACT:
 * All media URLs in carousel data use relative /api/uploads/** paths.
 * Legacy absolute URLs are normalized when loading data.
 * 
 * AUTH CONTRACT:
 * All requests include Authorization: Bearer <token> from localStorage.
 * GET /carousels returns only carousels owned by the authenticated user.
 * POST /carousels sets owner_id from the authenticated user.
 */

import { normalizeCarouselUrls } from './urlNormalizer';
import { getAuthHeaders } from './authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * @typedef {Object} Carousel
 * @property {string} id - Unique carousel ID
 * @property {string} name - Carousel name
 * @property {Array} slides - Array of slide data
 * @property {string} created_at - ISO timestamp
 * @property {string} updated_at - ISO timestamp
 */

/**
 * Fetch all carousels.
 * URLs in slides are normalized to relative /api/uploads/** paths.
 * @returns {Promise<Carousel[]>}
 */
export async function fetchCarousels() {
  const response = await fetch(`${BACKEND_URL}/api/carousels`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch carousels: ${response.status}`);
  }
  const carousels = await response.json();
  // Normalize URLs in all carousels
  return carousels.map(c => ({
    ...c,
    slides: normalizeCarouselUrls(c.slides),
  }));
}

/**
 * Create a new carousel.
 * @param {string|null} name - Carousel name (null for auto-generated: 001, 002, etc.)
 * @param {Array} slides - Initial slides (optional)
 * @returns {Promise<Carousel>}
 */
export async function createCarousel(name = null, slides = [], starterDemo = null) {
  const body = { slides };
  if (name !== null) {
    body.name = name;
  }
  if (starterDemo) {
    body.starter_demo = starterDemo;
  }
  
  const response = await fetch(`${BACKEND_URL}/api/carousels`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create carousel' }));
    throw new Error(error.detail || 'Failed to create carousel');
  }
  
  return response.json();
}

/**
 * Get a specific carousel by ID.
 * URLs in slides are normalized to relative /api/uploads/** paths.
 * @param {string} id - Carousel ID
 * @returns {Promise<Carousel>}
 */
export async function getCarousel(id) {
  const response = await fetch(`${BACKEND_URL}/api/carousels/${id}`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch carousel: ${response.status}`);
  }
  const carousel = await response.json();
  // Normalize URLs in slides
  return {
    ...carousel,
    slides: normalizeCarouselUrls(carousel.slides),
  };
}

/**
 * Update a carousel (name and/or slides).
 * 
 * If `updates.version` is included, the server checks for conflicts.
 * On 409 (version conflict), returns { conflict: true, serverCarousel }
 * instead of throwing, so the caller can reload gracefully.
 * 
 * @param {string} id - Carousel ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.name] - New name
 * @param {Array} [updates.slides] - Updated slides
 * @param {number} [updates.version] - Expected version (optimistic concurrency)
 * @returns {Promise<Carousel|{conflict: true, serverCarousel: Carousel}>}
 */
export async function updateCarousel(id, updates) {
  const response = await fetch(`${BACKEND_URL}/api/carousels/${id}`, {
    method: 'PUT',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(updates),
  });
  
  if (!response.ok) {
    // Legacy 409 handling (in case backend hasn't been updated)
    if (response.status === 409) {
      const data = await response.json().catch(() => ({}));
      const serverCarousel = data.detail?.carousel || null;
      return { conflict: true, serverCarousel };
    }
    const error = await response.json().catch(() => ({ detail: 'Failed to update carousel' }));
    throw new Error(error.detail || 'Failed to update carousel');
  }
  
  const data = await response.json();
  
  // New: 200 with _conflict flag (no red console line)
  if (data._conflict) {
    // Strip the conflict metadata, the rest IS the server carousel
    const { _conflict, server_version, your_version, ...serverCarousel } = data;
    return { conflict: true, serverCarousel, server_version, your_version };
  }
  
  return data;
}

/**
 * Delete a carousel.
 * @param {string} id - Carousel ID
 * @returns {Promise<void>}
 */
export async function deleteCarousel(id) {
  const response = await fetch(`${BACKEND_URL}/api/carousels/${id}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete carousel' }));
    throw new Error(error.detail || 'Failed to delete carousel');
  }
}

/**
 * Fetch only the version + updated_at for a carousel.
 * Lightweight endpoint used on carousel select + tab refocus.
 * @param {string} id - Carousel ID
 * @returns {Promise<{id: string, version: number, updated_at: string}>}
 */
export async function fetchCarouselVersion(id) {
  const response = await fetch(`${BACKEND_URL}/api/carousels/${id}/version`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch carousel version: ${response.status}`);
  }
  return response.json();
}

/**
 * Build the SSE URL for a carousel's event stream.
 * Used by useCarousels hook to open an EventSource connection.
 * @param {string} id - Carousel ID
 * @returns {string} Full URL to the SSE endpoint
 */
export function getCarouselEventsUrl(id) {
  return `${BACKEND_URL}/api/carousels/${id}/events`;
}
